

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewCreatorModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        full_name: '',
        date_of_birth: '',
        instagram_link: '',
        instagram_handle: '',
        phone: '',
        government_id_proof: '',
        location: '',
        operating_country: '',
        tax_id: '',
        tax_rate: '',
        creator_type: '',
        language: 'English'
    });

    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            setFormData({
                full_name: '', date_of_birth: '', instagram_link: '', instagram_handle: '',
                phone: '', government_id_proof: '', location: '', operating_country: '',
                tax_id: '', tax_rate: '', creator_type: '', language: 'English'
            });
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.full_name) errors.full_name = 'Full Name is required';
        if (!formData.date_of_birth) errors.date_of_birth = 'Date of Birth is required';
        if (!formData.instagram_link) errors.instagram_link = 'Instagram Link is required';
        if (!formData.instagram_handle) errors.instagram_handle = 'Instagram Handle is required';
        if (!formData.phone) errors.phone = 'Phone is required';
        if (!formData.government_id_proof) errors.government_id_proof = 'Government ID Proof is required';
        if (!formData.location) errors.location = 'Location is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                full_name: formData.full_name,
                date_of_birth: formData.date_of_birth,
                instagram_link: formData.instagram_link,
                instagram_handle: formData.instagram_handle,
                phone: formData.phone,
                government_id_proof: formData.government_id_proof,
                location: formData.location,
                operating_country: formData.operating_country || null,
                tax_id: formData.tax_id || null,
                tax_rate: formData.tax_rate ? parseFloat(formData.tax_rate) : null,
                creator_type: formData.creator_type || null,
                language: formData.language || 'English'
            };

            await onSave(payload);
            setFormData({
                full_name: '', date_of_birth: '', instagram_link: '', instagram_handle: '',
                phone: '', government_id_proof: '', location: '', operating_country: '',
                tax_id: '', tax_rate: '', creator_type: '', language: 'English'
            });
            onClose();
        } catch (error) {
            console.error('Error saving Creator:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Creator',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Creator</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.full_name}
                                        helperText={formErrors.full_name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date of Birth"
                                        name="date_of_birth"
                                        type="date"
                                        value={formData.date_of_birth}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        error={!!formErrors.date_of_birth}
                                        helperText={formErrors.date_of_birth}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        error={!!formErrors.phone}
                                        helperText={formErrors.phone}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Instagram Handle"
                                        name="instagram_handle"
                                        value={formData.instagram_handle}
                                        onChange={handleInputChange}
                                        error={!!formErrors.instagram_handle}
                                        helperText={formErrors.instagram_handle}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Instagram Link"
                                        name="instagram_link"
                                        type="url"
                                        value={formData.instagram_link}
                                        onChange={handleInputChange}
                                        error={!!formErrors.instagram_link}
                                        helperText={formErrors.instagram_link}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.location} required>
                                        <InputLabel>Location</InputLabel>
                                        <Select
                                            name="location"
                                            value={formData.location}
                                            onChange={handleInputChange}
                                            label="Location"

                                        >
                                            <MenuItem value=""><em>Select Location</em></MenuItem>
                                            <MenuItem value="United States">United States</MenuItem>
                                            <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                                            <MenuItem value="Australia">Australia</MenuItem>
                                            <MenuItem value="Canada">Canada</MenuItem>
                                            <MenuItem value="India">India</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Operating Country</InputLabel>
                                        <Select
                                            name="operating_country"
                                            value={formData.operating_country}
                                            onChange={handleInputChange}
                                            label="Operating Country"

                                        >
                                            <MenuItem value=""><em>Select Country</em></MenuItem>
                                            <MenuItem value="United States">United States</MenuItem>
                                            <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                                            <MenuItem value="Australia">Australia</MenuItem>
                                            <MenuItem value="Canada">Canada</MenuItem>
                                            <MenuItem value="India">India</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Creator Type</InputLabel>
                                        <Select
                                            name="creator_type"
                                            value={formData.creator_type}
                                            onChange={handleInputChange}
                                            label="Creator Type"

                                        >
                                            <MenuItem value=""><em>Select Type</em></MenuItem>
                                            <MenuItem value="Travel">Travel</MenuItem>
                                            <MenuItem value="Lifestyle">Lifestyle</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Language</InputLabel>
                                        <Select
                                            name="language"
                                            value={formData.language}
                                            onChange={handleInputChange}
                                            label="Language"

                                        >
                                            <MenuItem value="English">English</MenuItem>
                                            <MenuItem value="French">French</MenuItem>
                                            <MenuItem value="Spanish">Spanish</MenuItem>
                                            <MenuItem value="Deutsch">Deutsch</MenuItem>
                                            <MenuItem value="Italian">Italian</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Tax ID"
                                        name="tax_id"
                                        value={formData.tax_id}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Tax Rate (%)"
                                        name="tax_rate"
                                        type="number"
                                        value={formData.tax_rate}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Government ID Proof (URL)"
                                        name="government_id_proof"
                                        type="url"
                                        value={formData.government_id_proof}
                                        onChange={handleInputChange}
                                        error={!!formErrors.government_id_proof}
                                        helperText={formErrors.government_id_proof}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Creator'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewCreatorModal;

